/**
 * Data for the Plan & budget tab: every template, and what each one has
 * consumed so far in the current billing cycle.
 *
 * Budgets are monthly, so spend is always the *cycle*, never the page's
 * analysis period — a 3-month view must not make every budget look blown.
 * The per-template pages come from the same per-job reading the Cost Explorer
 * uses (measured, not allocated), fetched for the cycle window only and only
 * once the tab is opened.
 *
 * The fetch is by calendar date, but a billing cycle bought from Marketplace
 * starts mid-day (14 Oct 10:23Z), so the jobs of 00:00-10:23 that day, which
 * belong to the previous cycle, come back with it. The spend is therefore cut
 * again here at the cycle's exact start and end.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { templatesApi } from '../../services/api'
import { buildExplorer, DEFAULT_PRICING } from '../../utils/costExplorer'
import { useCostExplorer } from './useCostExplorer'

/** The cycle as a { from, to } custom range: its first day through today. */
export function cycleRange(cycle, now = new Date()) {
  const start = cycle?.start ? new Date(cycle.start) : null
  if (!start || Number.isNaN(start.getTime())) return null
  const from = start.toISOString().slice(0, 10)
  const today = now.toISOString().slice(0, 10)
  return { from, to: today < from ? from : today }
}

const isoDay = d => d.toISOString().slice(0, 10)

/**
 * Today's UTC date, kept current: it changes at 00:00 UTC even if the page is
 * left open, so anything derived from "today" follows the clock instead of
 * staying on the day the page loaded.
 */
export function useUtcDay() {
  const [day, setDay] = useState(() => isoDay(new Date()))
  useEffect(() => {
    const now = new Date()
    const nextMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    // A second past midnight, so the new day is certain to have started.
    const id = setTimeout(() => setDay(isoDay(new Date())), Math.max(1000, nextMidnight - now.getTime() + 1000))
    // A sleeping laptop or a background tab can hold the timer back; coming
    // back to the tab re-reads the clock.
    const onVisible = () => { if (!document.hidden) setDay(isoDay(new Date())) }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [day])
  return day
}

export function usePlanBudgetData(plan, pricing, enabled) {
  const cycleStart = plan?.cycle?.start
  const cycleEnd = plan?.cycle?.end
  const today = useUtcDay()
  // Recomputed per cycle and per day, not per render: a fresh object every
  // render would look like a new period to the explorer and refetch forever.
  const custom = useMemo(
    () => cycleRange({ start: cycleStart }, new Date(`${today}T00:00:00Z`)),
    [cycleStart, today],
  )
  const explorer = useCostExplorer('custom', custom, enabled && Boolean(custom))

  const [templates, setTemplates] = useState({ list: null, loading: false, error: null })
  const requested = useRef(false)
  const seq = useRef(0)

  const loadTemplates = useCallback(async () => {
    const id = ++seq.current
    setTemplates(t => ({ ...t, loading: true, error: null }))
    try {
      const res = await templatesApi.list()
      if (id !== seq.current) return
      setTemplates({ list: res?.data?.templates || [], loading: false, error: null })
    } catch (err) {
      if (id !== seq.current) return
      setTemplates(t => ({ ...t, loading: false, error: err }))
    }
  }, [])

  useEffect(() => {
    if (!enabled || requested.current) return
    requested.current = true
    loadTemplates()
  }, [enabled, loadTemplates])

  // The cycle's own instants, not the dates the fetch was cut on.
  const cycleBounds = useMemo(() => {
    const start = cycleStart ? new Date(cycleStart) : null
    const end = cycleEnd ? new Date(cycleEnd) : null
    if (!start || Number.isNaN(start.getTime())) return null
    return { start, end: end && !Number.isNaN(end.getTime()) ? new Date(end.getTime() - 1) : null }
  }, [cycleStart, cycleEnd])

  const spend = useMemo(() => {
    const d = explorer.data
    if (!d) return null
    return buildExplorer({
      jobs: d.jobs,
      requests: d.requests,
      users: d.users,
      pricing: pricing || DEFAULT_PRICING,
      bounds: cycleBounds || d.bounds,
    }).dims.template
  }, [explorer.data, pricing, cycleBounds])

  const { reload: reloadExplorer } = explorer
  const reload = useCallback(() => {
    if (!requested.current) return
    loadTemplates()
    reloadExplorer()
  }, [loadTemplates, reloadExplorer])

  return {
    templates: templates.list,
    templatesLoading: templates.loading,
    templatesError: templates.error,
    spend,
    spendLoading: explorer.loading,
    spendError: explorer.error,
    spendProgress: explorer.progress,
    truncated: Boolean(explorer.data?.truncated),
    reload,
  }
}
